/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * One line in the dealer directory: a person AT one master dealer.
 *
 * The directory used to show a person once, with every master dealer ID they
 * hold joined into a single cell. That made the two things you need to act on
 * indistinguishable: somebody can be the principal of one master dealer and an
 * ordinary team member of another, and only the team-member entry may be
 * removed. With one row per master dealer each can be judged, and deleted, on
 * its own.
 */
export interface DirectoryRow {
  user: any
  /** Index of the user in the original list, for the edit handler. */
  userIndex: number
  masterDealerId?: string
  masterDealerName?: string
  channels: string[]
  /** Principal OF THIS master dealer, not of the person. */
  isPrincipalHere: boolean
  principalStatusKnown: boolean
  /** How many master dealers this person holds, across all their rows. */
  masterDealerCount: number
}

const normalized = (value?: string): string =>
  typeof value === 'string' ? value.trim().toUpperCase() : ''

const sameEmail = (a?: string, b?: string): boolean =>
  !!normalized(a) && normalized(a) === normalized(b)

const masterDealerOf = (association: any) =>
  Array.isArray(association?.masterDealer)
    ? association.masterDealer.find(
        (dealer: any) =>
          normalized(dealer?.masterDealerId) ===
          normalized(association.masterDealerId)
      )
    : undefined

const channelsOf = (association: any): string[] => {
  const own = Array.isArray(association?.channels) ? association.channels : []
  const masterDealer = masterDealerOf(association)
  const fromMasterDealer = Array.isArray(masterDealer?.channels)
    ? masterDealer.channels
    : []
  const channels = own.length ? own : fromMasterDealer

  return Array.from(
    new Set(
      channels
        .filter((c: any) => typeof c === 'string' && c.trim().length)
        .map((c: string) => c.trim().toUpperCase())
    )
  )
}

/**
 * Whether this person is the principal of THIS master dealer.
 *
 * `MasterDealer.principals` is the authority, but it is not populated
 * everywhere yet, so where it is missing we fall back to the global principal
 * list. That is deliberately the cautious direction: nobody becomes deletable
 * who was not already, and rows get more precise as the backfill lands. The
 * incoming dealer file is what writes `principals`, so the master dealers this
 * distinction actually matters for are the ones that will have it.
 */
const isPrincipalOf = (
  association: any,
  email?: string,
  globalPrincipalEmails?: string[]
): boolean | undefined => {
  const principals = masterDealerOf(association)?.principals

  if (Array.isArray(principals)) {
    return principals.some((p: string) => sameEmail(p, email))
  }

  return Array.isArray(globalPrincipalEmails)
    ? globalPrincipalEmails.some((p) => sameEmail(p, email))
    : undefined
}

/** Flatten the directory into one row per person per master dealer. */
export const toDirectoryRows = (
  users: any[] | null | undefined,
  globalPrincipalEmails?: string[]
): DirectoryRow[] => {
  if (!Array.isArray(users)) {
    return []
  }

  const rows: DirectoryRow[] = []

  users.forEach((user, userIndex) => {
    const associations = user?.dealer?.associatedMasterDealers
    const usable = (Array.isArray(associations) ? associations : []).filter(
      (association: any) => !!association?.masterDealerId?.trim?.()
    )

    // Somebody with no usable association still belongs in the directory -
    // dropping them would hide the person entirely.
    if (!usable.length) {
      const principal = isPrincipalOf(
        undefined,
        user?.email,
        globalPrincipalEmails
      )
      rows.push({
        user,
        userIndex,
        masterDealerId: undefined,
        masterDealerName: undefined,
        channels: [],
        isPrincipalHere: principal === true,
        principalStatusKnown: principal !== undefined,
        masterDealerCount: 0,
      })
      return
    }

    const seen = new Map<string, DirectoryRow>()

    usable.forEach((association: any) => {
      const masterDealerId = normalized(association.masterDealerId)
      const principal = isPrincipalOf(
        association,
        user?.email,
        globalPrincipalEmails
      )
      const existing = seen.get(masterDealerId)
      if (existing) {
        existing.channels = Array.from(
          new Set([...existing.channels, ...channelsOf(association)])
        )
        existing.isPrincipalHere =
          existing.isPrincipalHere || principal === true
        existing.principalStatusKnown =
          existing.principalStatusKnown && principal !== undefined
        return
      }

      const row: DirectoryRow = {
        user,
        userIndex,
        masterDealerId,
        masterDealerName:
          masterDealerOf(association)?.name?.trim() || undefined,
        channels: channelsOf(association),
        isPrincipalHere: principal === true,
        principalStatusKnown: principal !== undefined,
        masterDealerCount: 0,
      }
      seen.set(masterDealerId, row)
      rows.push(row)
    })
  })

  // Fill in the count now that every row exists, so a row knows whether
  // removing it would leave the person with any master dealer at all.
  const countByUser = new Map<number, number>()
  rows.forEach((row) => {
    if (!row.masterDealerId) return
    countByUser.set(row.userIndex, (countByUser.get(row.userIndex) || 0) + 1)
  })

  return rows.map((row) => ({
    ...row,
    masterDealerCount: countByUser.get(row.userIndex) || 0,
  }))
}

/**
 * Whether this row may be removed on its own.
 *
 * A principal's row never may - that is by design, it is what stops somebody
 * being quietly detached from the master dealer they are the principal of. A
 * team-member row may, provided the person holds another master dealer:
 * removing their last one would leave a dealer attached to nothing, which is a
 * whole-user deletion and a different decision.
 */
export const canRemoveAssociation = (row: DirectoryRow): boolean =>
  row.principalStatusKnown &&
  !row.isPrincipalHere &&
  !!row.masterDealerId &&
  row.masterDealerCount > 1

/** Build the existing dealer PATCH contract without joined master-dealer data. */
/**
 * The association list to send, with the removed master dealer left out.
 *
 * On its own this is the safe payload: the backend only touches Co-Op
 * allowances when the body carries a userEmail, so a body of just associations
 * cannot alter them. Allowance syncing is layered on top by
 * withAllowanceSync, and only when the current allowances are actually known.
 */
export interface AssociationRemoval {
  associatedMasterDealers: any[]
  hasCoop?: boolean
  userEmail?: string
  coopAllowances?: any[]
}

/**
 * Build the association list for detaching one master dealer.
 *
 * Re-validates removability off the record it was handed rather than trusting
 * the row that was clicked, so a stale screen cannot talk it into removing a
 * principal.
 */
export const createAssociationRemoval = (
  user: any,
  masterDealerId: string,
  principalEmails?: string[]
): AssociationRemoval | undefined => {
  const id = normalized(masterDealerId)
  const row = toDirectoryRows([user], principalEmails).find(
    (item) => item.masterDealerId === id
  )
  if (
    !row ||
    !canRemoveAssociation(row) ||
    !user?.dealer?._id ||
    !user?.email
  ) {
    return undefined
  }

  const associatedMasterDealers = user.dealer.associatedMasterDealers
    .filter(
      (association: any) => normalized(association?.masterDealerId) !== id
    )
    .map((association: any) => {
      // The joined master dealer is read-only decoration from the lookup.
      // Sending it back would write it into the association subdocument.
      const patch = { ...association }
      delete patch.masterDealer
      return patch
    })

  return { associatedMasterDealers }
}

/** Whether an allowance belongs to the master dealer being removed. */
const belongsTo = (allowanceMasterDealerId: string, id: string): boolean => {
  const allowanceId = normalized(allowanceMasterDealerId)

  // Allowances use channel-suffixed IDs (e.g. 2252L for 2252 on LSP).
  return (
    allowanceId === id ||
    (allowanceId.slice(0, -1) === id &&
      /^[LMDIEN]$/.test(allowanceId.slice(-1)))
  )
}

/**
 * Add the Co-Op allowance sync to a removal payload.
 *
 * Only call this with allowances that were read successfully. The backend
 * treats the list as authoritative and deletes any existing allowance missing
 * from it, so a partial list would clear allowances on the accounts the dealer
 * keeps. Where the allowances cannot be read, send the payload without this:
 * the association still goes, and the allowances are left untouched rather
 * than guessed at.
 */
export const withAllowanceSync = (
  payload: AssociationRemoval,
  user: any,
  masterDealerId: string,
  currentAllowances: any[]
): AssociationRemoval => {
  const id = normalized(masterDealerId)

  return {
    ...payload,
    hasCoop: user?.dealer?.hasCoop,
    userEmail: user?.email,
    // Keep every other allowance unchanged, and explicitly delete only the
    // ones belonging to the master dealer being removed.
    coopAllowances: currentAllowances.map((allowance: any) =>
      belongsTo(allowance?.masterDealerId, id)
        ? { ...allowance, delete: true }
        : { ...allowance }
    ),
  }
}

export interface ScopedMasterDealerChange {
  /** The ids to send: everything they hold already, plus anything added. */
  masterDealerIds: string[]
  added: string[]
  /** Ids the form dropped. Refused - removal belongs to the row's own button. */
  removalAttempted: string[]
}

/**
 * Work out what an edit opened from a directory row may change about a
 * person's accounts.
 *
 * Additions are fine. Removals are not: the association list is sent whole and
 * replaces what is stored, so a removal made here would detach an account
 * without any of the protection the rows carry - including the principal
 * account the rows exist to keep safe. Removing happens on the row itself,
 * where principal standing is checked.
 *
 * Attempted removals are reported rather than quietly dropped, so the person
 * doing it is told where to go instead of watching a change fail to stick.
 */
/** The field holds either bare ids or association objects, so accept both. */
type MasterDealerRef = string | { masterDealerId?: string } | null | undefined

const idOf = (ref: MasterDealerRef): string =>
  typeof ref === 'string' ? normalized(ref) : normalized(ref?.masterDealerId)

export const mergeScopedMasterDealerIds = (
  existingIds: MasterDealerRef[] | null | undefined,
  formIds: MasterDealerRef[] | null | undefined
): ScopedMasterDealerChange => {
  const existing = (Array.isArray(existingIds) ? existingIds : [])
    .map(idOf)
    .filter(Boolean)
  const form = (Array.isArray(formIds) ? formIds : []).map(idOf).filter(Boolean)

  const existingSet = new Set(existing)
  const formSet = new Set(form)

  const added = form.filter((id) => !existingSet.has(id))
  const removalAttempted = existing.filter((id) => !formSet.has(id))

  return {
    masterDealerIds: [...existing, ...added],
    added,
    removalAttempted,
  }
}
